import { TextAreaField } from '@aws-amplify/ui-react'
import { DownloadForOfflineOutlined } from '@mui/icons-material'
import { Alert, Stack } from '@mui/material'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AxiosError } from 'axios'
import type { DateTime as DateTimeType } from 'luxon'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getCountryAsAlpha2Code } from '@/constants/country'
import { TOMORROW } from '@/constants/dateTime'
import { useAlertContext } from '@/contexts/AlertContext'
import SubmitButton from '@/features/bidding/components/SubmitButton'
import { useBiddingContext } from '@/features/bidding/contexts/BiddingContext'
import {
  useGetMfrrEnergyBidMutation,
  useIsGettingMfrrEnergyBid,
} from '@/features/bidding/hooks/useGetMfrrEnergyBidMutation'

function ExportMfrrEnergyBid() {
  const { t } = useTranslation()

  const { pushAlert } = useAlertContext()
  const { selectedCountry } = useBiddingContext()
  const isGettingMfrrEnergyBid = useIsGettingMfrrEnergyBid()
  const { getMfrrEnergyBid } = useGetMfrrEnergyBidMutation()

  const [tsvRows, setTsvRows] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [bidDate, setBidDate] = useState<DateTimeType>(TOMORROW)

  const dialogProps = {
    title: t('bidding.export_mfrr_bid.dialog.title'),
    description: t('bidding.export_mfrr_bid.dialog.description'),
  }

  const { i18n } = useTranslation()

  async function handleDownloadButtonClick() {
    const countryCode = getCountryAsAlpha2Code(selectedCountry)

    // Clean any content that was in the text area and error message before fetching new data
    setTsvRows('')
    setErrorMessage('')

    await getMfrrEnergyBid({ countryCode, deliveryDay: bidDate.toISODate() ?? '' })
      .then((response) => {
        // Copy the data to clipboard
        navigator.clipboard.writeText(response.data)

        setTsvRows(response.data)
        pushAlert({
          message: t('bidding.export_mfrr_bid.energy.download_successful.text'),
          severity: 'success',
          title: t('bidding.export_mfrr_bid.energy.download_successful.title'),
        })
      })
      .catch((error) => {
        if (error instanceof AxiosError) {
          if (error.response?.status === 404) {
            pushAlert({
              message: t('bidding.export_mfrr_bid.energy.download_failed.not_found'),
              severity: 'error',
            })

            return
          }

          if (error.response?.data !== undefined) {
            setErrorMessage(error.response.data)
          } else {
            setErrorMessage(error.message ?? t('bidding.export_mfrr_bid.download_failed.unknown_error'))
          }
        } else {
          setErrorMessage(t('bidding.export_mfrr_bid.download_failed.unknown_error'))
        }
      })
  }

  return (
    <Stack direction="column" spacing={2}>
      {errorMessage != '' && (
        <Alert data-testid="server-error" severity="error">
          <p>{t('bidding.export_mfrr_bid.download_failed.error_message_from_server')}</p>
          <p>{errorMessage}</p>
        </Alert>
      )}

      <Stack alignItems="center" direction="row" spacing={2}>
        <LocalizationProvider adapterLocale={i18n.language} dateAdapter={AdapterLuxon}>
          <DatePicker
            enableAccessibleFieldDOMStructure={false}
            label="Delivery date"
            sx={{ width: 180 }}
            value={bidDate}
            onChange={(newValue) => setBidDate(newValue ?? TOMORROW)}
          />
        </LocalizationProvider>

        <SubmitButton
          buttonProps={{
            startIcon: <DownloadForOfflineOutlined />,
            disabled: isGettingMfrrEnergyBid,
          }}
          dialogProps={dialogProps}
          shouldShowConfirmation={false}
          onSubmit={handleDownloadButtonClick}
        >
          {t('bidding.export_mfrr_bid.energy.download_button')}
        </SubmitButton>
      </Stack>

      <TextAreaField
        data-testid="tsv-rows"
        label={t('bidding.export_mfrr_bid.energy.tsv')}
        placeholder={t('bidding.export_mfrr_bid.energy.tsv_placeholder')}
        resize="vertical"
        rows={100}
        value={tsvRows}
        onChange={(e) => setTsvRows(e.target.value)}
      />
    </Stack>
  )
}

export default ExportMfrrEnergyBid
